import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
} from 'axios';

import {
  HttpClientError,
  HttpError,
  HttpNetworkError,
  HttpServerError,
  HttpTimeoutError,
} from './http.errors';

export interface HttpClientConfig {
  baseURL: string;
  timeout?: number;
  retries?: number;
  retryDelay?: number;
  headers?: Record<string, string>;
  /** Async function to get auth token for Authorization header */
  getAuthToken?: () => Promise<string | null>;
  /** Called when a request fails (after all retries exhausted) */
  onError?: (error: HttpError) => void;
  /** Called before each retry attempt */
  onRetry?: (attempt: number, maxAttempts: number, error: HttpError) => void;
}

/** Per-request options for lifecycle callbacks */
export interface HttpRequestOptions {
  /** Called when the request starts */
  onPending?: (url: string) => void;
  /** Called when the request succeeds */
  onSuccess?: (url: string) => void;
  /** Called when the request fails (in addition to global onError) */
  onError?: (error: HttpError) => void;
}

export interface HttpResponse<T = any> {
  data: T;
  status: number;
  statusText: string;
  headers: Record<string, any>;
}

const DEFAULT_CONFIG: Partial<HttpClientConfig> = {
  timeout: 30000,
  retries: 0,
  retryDelay: 1000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
};

export class HttpClient {
  private axiosInstance: AxiosInstance;
  private config: HttpClientConfig;

  constructor(config: HttpClientConfig) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.axiosInstance = axios.create({
      baseURL: this.config.baseURL,
      timeout: this.config.timeout,
      headers: {
        ...DEFAULT_CONFIG.headers,
        ...config.headers,
      },
    });
    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    // Request interceptor for auth token
    this.axiosInstance.interceptors.request.use(
      async (config) => {
        if (this.config.getAuthToken) {
          const token = await this.config.getAuthToken();
          if (token) {
            config.headers['Authorization'] = `Bearer ${token}`;
          }
        }
        return config;
      },
      (error: AxiosError) => {
        return Promise.reject(error);
      }
    );
  }

  private shouldRetry(
    error: any,
    attempt: number,
    maxAttempts: number
  ): boolean {
    if (attempt >= maxAttempts) {
      return false;
    }

    // Don't retry client errors (4xx)
    if (
      error.response &&
      error.response.status >= 400 &&
      error.response.status < 500
    ) {
      return false;
    }

    // Retry server errors (5xx) and network errors
    return true;
  }

  private createHttpError(error: any, url: string): HttpError {
    // If the server responded with an error
    if (error.response) {
      const errorMessage = `HTTP request failed with status ${error.response.status}: ${
        error.response.data?.message || error.response.statusText
      }`;

      if (error.response.status >= 500) {
        return new HttpServerError(
          errorMessage,
          error.response.status,
          error.response.statusText,
          error.response.data,
          url
        );
      }

      return new HttpClientError(
        errorMessage,
        error.response.status,
        error.response.statusText,
        error.response.data,
        url
      );
    }

    // If the request was made but no response was received
    if (error.request) {
      if (error.code === 'ECONNABORTED') {
        return new HttpTimeoutError(
          `HTTP request timed out after ${this.config.timeout}ms`,
          this.config.timeout || DEFAULT_CONFIG.timeout!,
          url
        );
      }

      return new HttpNetworkError(
        `HTTP request failed: Network error - ${error.message}`,
        url
      );
    }

    // Something else happened
    return new HttpError(
      `HTTP request failed: ${error.message || 'Unknown error'}`,
      {
        url,
      }
    );
  }

  private async executeWithRetry<T>(
    requestFn: () => Promise<AxiosResponse<T>>,
    url: string,
    options?: HttpRequestOptions
  ): Promise<HttpResponse<T>> {
    const maxAttempts = (this.config.retries || 0) + 1;
    let attempt = 0;

    // Call onPending callback
    if (options?.onPending) {
      options.onPending(url);
    }

    while (attempt < maxAttempts) {
      try {
        const response = await requestFn();

        // Call onSuccess callback
        if (options?.onSuccess) {
          options.onSuccess(url);
        }

        return {
          data: response.data,
          status: response.status,
          statusText: response.statusText,
          headers: response.headers as Record<string, any>,
        };
      } catch (error: any) {
        attempt++;

        const httpError = this.createHttpError(error, url);

        if (!this.shouldRetry(error, attempt, maxAttempts)) {
          // Call per-request onError callback
          if (options?.onError) {
            options.onError(httpError);
          }
          // Call global onError callback
          if (this.config.onError) {
            this.config.onError(httpError);
          }
          throw httpError;
        }

        // Call onRetry callback
        if (this.config.onRetry) {
          this.config.onRetry(attempt, maxAttempts, httpError);
        }

        // Wait before retry
        if (this.config.retryDelay && attempt < maxAttempts) {
          await new Promise((resolve) =>
            setTimeout(resolve, this.config.retryDelay)
          );
        }
      }
    }

    // This should never be reached
    const finalError = new HttpError(
      `HTTP request failed after ${maxAttempts} attempts`,
      {
        url,
      }
    );
    if (options?.onError) {
      options.onError(finalError);
    }
    if (this.config.onError) {
      this.config.onError(finalError);
    }
    throw finalError;
  }

  /**
   * Sends a GET request.
   */
  public async get<T = any>(
    url: string,
    config?: AxiosRequestConfig,
    options?: HttpRequestOptions
  ): Promise<HttpResponse<T>> {
    return this.executeWithRetry<T>(
      () => this.axiosInstance.get<T>(url, config),
      url,
      options
    );
  }

  /**
   * Sends a GET request with query parameters.
   */
  public async getWithParams<T = any>(
    url: string,
    params: Record<string, any>,
    config?: AxiosRequestConfig,
    options?: HttpRequestOptions
  ): Promise<HttpResponse<T>> {
    return this.executeWithRetry<T>(
      () => this.axiosInstance.get<T>(url, { ...config, params }),
      url,
      options
    );
  }

  /**
   * Sends a POST request.
   */
  public async post<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
    options?: HttpRequestOptions
  ): Promise<HttpResponse<T>> {
    return this.executeWithRetry<T>(
      () => this.axiosInstance.post<T>(url, data, config),
      url,
      options
    );
  }

  /**
   * Sends a PUT request.
   */
  public async put<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
    options?: HttpRequestOptions
  ): Promise<HttpResponse<T>> {
    return this.executeWithRetry<T>(
      () => this.axiosInstance.put<T>(url, data, config),
      url,
      options
    );
  }

  /**
   * Sends a DELETE request.
   */
  public async delete<T = any>(
    url: string,
    config?: AxiosRequestConfig,
    options?: HttpRequestOptions
  ): Promise<HttpResponse<T>> {
    return this.executeWithRetry<T>(
      () => this.axiosInstance.delete<T>(url, config),
      url,
      options
    );
  }

  /**
   * Sends a PATCH request.
   */
  public async patch<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
    options?: HttpRequestOptions
  ): Promise<HttpResponse<T>> {
    return this.executeWithRetry<T>(
      () => this.axiosInstance.patch<T>(url, data, config),
      url,
      options
    );
  }
}

export {
  HttpClientError,
  HttpError,
  HttpNetworkError,
  HttpServerError,
  HttpTimeoutError,
} from './http.errors';
